import { useCallback, useState } from "react";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import { Order } from "../../../api/order";
import useOrdersQuery from "../hooks/useOrdersQuery";
import OrderModal from "./OrderModal";
import DSBox from "../../system-design/DSBox";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import PageHeader from "components/PageHeader";
import FilterBar from "components/FilterBar";
import { debounce } from "lodash";

function Orders() {
  const [textSearch, setTextSearch] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [sortState, setSortState] = useState<Record<string, 1 | -1>>({});

  const handleFreeSearch = useCallback(
    debounce((s: string) => setTextSearch(s), 500),
    [],
  );

  const {
    data: orders,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useOrdersQuery(textSearch);

  const orderTableColumns: Column<Order>[] = [
    { label: "Order Name", field: "orderName", sortable: true },
    { label: "Customer Name", field: "customerName", sortable: true },
    { label: "Customer Email", field: "customerEmail", sortable: true },
    { label: "Order ID", field: "id", sortable: true },
    { label: "Number of Clocks", field: "clocks", sortable: true },
    { label: "Number of Collections", field: "collections", sortable: true },
    { label: "Has Gift", field: "id" as keyof Order, sortable: false },
  ];

  const rowBuilder = (order: Order, columns: Column<Order>[]) => {
    return (
      <>
        {columns.map((col) => {
          if (col.field === "orderName") {
            return (
              <DSTableCell key={col.field as string}>
                {order.orderName}
              </DSTableCell>
            );
          }
          if (col.field === "customerName") {
            return (
              <DSTableCell key={col.field as string}>
                {order.customerName}
              </DSTableCell>
            );
          }
          if (col.field === "customerEmail") {
            return (
              <DSTableCell key={col.field as string}>
                {order.customerEmail}
              </DSTableCell>
            );
          }
          if (col.label === "Has Gift") {
            return (
              <DSTableCell key={col.field as string}>
                {"gift" in order && order.gift ? "✓" : "-"}
              </DSTableCell>
            );
          }
          if (col.field === "id") {
            return (
              <DSTableCell key={col.field as string}>{order.id}</DSTableCell>
            );
          }
          if (col.field === "clocks") {
            const total = order.clocks?.length || 0;
            const assigned =
              order.clocks?.filter((clock) => clock.clockId).length || 0;
            return (
              <DSTableCell key={col.field as string}>
                {total ? `${assigned}/${total}` : 0}
              </DSTableCell>
            );
          }
          if (col.field === "collections") {
            return (
              <DSTableCell key={col.field as string}>
                {order.collections?.length || 0}
              </DSTableCell>
            );
          }
          return <DSTableCell key={col.field as string}>-</DSTableCell>;
        })}
      </>
    );
  };

  const handleRowClick = (order: Order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const handleSort = (field: keyof Order) => {
    setSortState((prevState) => {
      const currentSort = prevState[field as string];
      if (!currentSort) {
        return { [field]: 1 };
      } else if (currentSort === 1) {
        return { [field]: -1 };
      } else {
        const newState = { ...prevState };
        delete newState[field as string];
        return newState;
      }
    });
  };

  return (
    <>
      <PageHeader
        overline="Commerce"
        title="Orders"
        subtitle="Track device orders, customers, collections, and gift attachments."
      />
      <FilterBar
        search={{
          value: searchInput,
          onChange: (value) => {
            setSearchInput(value);
            handleFreeSearch(value);
          },
          placeholder: "Search by order name, email, customer name, or ID…",
        }}
      />

      {isLoading && !orders?.length ? (
        <DSBox sx={{ textAlign: "center", p: "50px" }}>
          <DSCircularProgress />
        </DSBox>
      ) : (
        orders && (
          <DSBaseTable
            data={orders}
            columns={orderTableColumns}
            rowBuilder={rowBuilder}
            onPressRow={handleRowClick}
            onSort={handleSort}
            sortState={sortState}
            fetchMore={
              hasNextPage && !isFetchingNextPage ? fetchNextPage : undefined
            }
          />
        )
      )}

      <OrderModal
        show={showModal}
        order={selectedOrder}
        onClose={() => {
          setShowModal(false);
          setSelectedOrder(null);
        }}
      />
    </>
  );
}

export default Orders;
